import { api } from "../lib/api.js";
import { manageBilling, onCheckoutEvent, startCheckout } from "../lib/billing.js";
import { renderChatLinks } from "./chat-links.js";

/**
 * Account: verification, plan, pairing, billing.
 *
 * These four sat unbuilt while their endpoints existed, which meant a signed-up
 * user had no way to confirm their address, connect Stream-Cut Studio, or pay. They belong
 * on one screen because they are the same errand — getting an account from
 * "registered" to "working".
 */

interface Me {
  email: string;
  display_name: string | null;
  email_verified: boolean;
  plan: { id: string; name: string };
  storage: { quota_bytes: number; used_bytes: number };
  recordings: { used_this_month: number; limit: number | null; remaining: number | null; at_limit: boolean };
  pending: { sessions: number; reactions: number };
  access: {
    state: "unverified" | "trialing" | "active" | "past_due" | "expired";
    can_start_session: boolean;
    can_upload: boolean;
    can_redeem_pending: boolean;
    trial_ends_at: string | null;
    trial_days_left: number | null;
  } | null;
}

interface PlanRow {
  id: string;
  display_name: string;
  price_cents: number;
  /** Null is unlimited, which is what Creator and Pro are. */
  max_clips_per_recording?: number | null;
  max_recordings_per_month?: number | null;
  storage_quota_bytes: number;
  clip_retention_days: number;
}

interface Device {
  id: string;
  label: string | null;
  created_at: string;
  last_used_at: string | null;
}

const gb = (n: number) => (n / 1e9).toFixed(n < 1e10 ? 1 : 0);

/** Never inject a server string into innerHTML without this. */
const esc = (s: string) =>
  s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );

// Module scope, not inside accountView: the page template calls includedBlock()
// before the function body reaches this list, and as a const inside the function
// it was not yet initialised there, so Account threw on every load.
/**
 * Everything a subscription includes, stated where it is bought.
 *
 * The website lists all of this, but somebody deciding whether to pay is
 * standing here, not there, and "Creator — $29/mo" on its own asks them to
 * remember a page they read once. Written out in full so the decision can be
 * made with the facts in front of it.
 *
 * Grouped and collapsed rather than a flat list of forty items: open, it is the
 * answer to "what am I paying for"; closed, it is one line and does not bury the
 * buttons underneath it.
 *
 * Kept in step with the site's own list by hand, which is a real cost. The
 * alternative is a feature catalogue in the database, and that is the shape that
 * invites per-plan flags -- the one thing the pricing promises never to have.
 */
const INCLUDED: { group: string; items: string[] }[] = [
  {
    group: "Marking and cutting",
    items: [
      "Mark reactions live with hotkeys that work while OBS or a game has focus",
      "Timed marks \u2014 5 or 10 minutes around something you did not see coming",
      "Clips cut on your own PC at the end of a session; only the clips upload",
      "Review every clip before anything is sent, and nudge an edge a second at a time",
      "Or send the whole recording and have it cut here",
      "Edit a boundary afterwards and re-cut; your correction is never overwritten",
      "Mark from your own phone when your hands are on a controller",
      "A Stream Deck, macro pad or USB foot pedal marks a moment too",
    ],
  },
  {
    group: "How clips come out",
    items: [
      "Portrait 9:16, stacked with your camera so you are never cropped out",
      "Burned-in captions, with the transcript kept beside the clip as a .vtt",
      "Your own logo, in whichever corner you choose",
      "Pin the clips you want kept past your plan's retention",
      "Keep both shapes of a moment \u2014 landscape for YouTube, portrait for TikTok",
      "Join the clips you kept into one video, copied rather than re-encoded",
    ],
  },
  {
    group: "Your audience",
    items: [
      "A room code your viewers open on their phones \u2014 no app, no account",
      "A live reaction meter, and every clip ranked by how hard the crowd reacted",
      "Moments the crowd found come back as suggestions to approve, never cut for you",
      "An overlay for your OBS scene, and suggestions from your own mic level",
    ],
  },
  {
    group: "On screen",
    items: [
      "Camera background removal, blur or green screen",
      "Drag and resize the camera and the clip, then lock them",
      "A lip-syncing avatar with head tracking, in place of your camera",
      "Stage backgrounds, a soundboard on global hotkeys, mic and speaker routing",
      "Milestone alerts, pacing nudges, a monitor window and clean-output mode",
    ],
  },
  {
    group: "Games",
    items: [
      "Steam, Epic and GOG games found automatically, with their artwork",
      "One button: launch, switch scene, record, start the session",
      "Per-game OBS scene presets, and clips tagged with what you played",
      "A per-game comparison of how your crowd reacted, from your own data",
      "Viewers ask for a slot in your game; you let them in one at a time",
      "See who is in the game, and remove one so their phone cannot read your code",
      "Place each overlay piece: six corners, your own size, or hidden entirely",
      "The queue says what you are playing, and how that game wants to be joined",
      "Free browser games a viewer can join with nothing to install and no account",
      "Remote Play Together: viewers play your copy of a game without owning it",
      "Six built-in avatar characters, so the PNGTuber works before you draw anything",
    ],
  },
  {
    group: "OBS and going live",
    items: [
      "Finds and connects your OBS, and builds the two scenes for you",
      "Starts and stops your recording, and syncs to the frame",
      "Multistream to Twitch, YouTube and Kick at once without re-encoding",
      "A portrait feed for TikTok",
      "Warnings for the things that ruin a stream: not recording, a full drive, a dead mic",
    ],
  },
  {
    group: "After the stream",
    items: [
      "A summary while you are still sitting there, against your own recent streams",
      "A clip library sorted by what the crowd liked most, filtered by game",
      "Plan the posting here — which clips, how many a day, which days, what times",
      "Then one file to import into Postly, with the clip, the caption and the time on every row",
      "Search your clips by what you said in them, once captions wrote it down",
      "Find the loud moments in a recording made before you had Stream-Cut",
      "Told before anything expires, never after",
      "Cancel and your clips stay downloadable; recorded sessions can be cut for 30 days",
    ],
  },
  {
    group: "Getting paid",
    items: [
      "Let viewers pay to get their video reacted to, to promote their product, or both",
      "A button to pay you on the same page your viewers tap reactions on",
      "Your prices shown to viewers before they pay, so nobody has to guess",
      "A separate button for people who just want to tip, expecting nothing back",
      "Your recent games first, and a filter for ones you have not streamed lately",
      "A paid promotion joins your rotation for one stream, always labelled as paid",
      "They can send a video with it, or just a link — either way it is a button on every phone",
      "Runs on your own Ko-fi, Buy Me a Coffee or StreamElements page \u2014 any platform, no approval",
      "The money goes straight to you; Stream-Cut never holds it or takes a cut",
      "Their file is checked first, and you watch it before anything plays",
      "Approved videos are fetched before you go live, with the credit on screen",
    ],
  },
  {
    group: "Other streamers, and promotion",
    items: [
      "A board for finding someone to play with. No private messages, ever",
      "A profile that stays private until you publish it",
      "Weekly challenges counted from your own clips",
      "A promotion slot with a button on your viewers' phones",
      "Or press a product to put it on screen now, and press it again to take it down",
      "Short product videos loop while they are up, in a corner, over whatever you are streaming",
      "The button says Shop now, Follow now or Watch now to fit the link — or whatever you write",
    ],
  },
];

function includedBlock(): string {
  const groups = INCLUDED.map(
    (g) => `<div>
        <h3>${esc(g.group)}</h3>
        <ul>${g.items.map((i) => `<li>${esc(i)}</li>`).join("")}</ul>
      </div>`,
  ).join("");

  return `<details class="included">
      <summary>Everything included, on every plan \u2014 the tiers differ only by
        how much you stream, store and cut</summary>
      <div class="included-grid">${groups}</div>
      <p class="meta">Nothing is locked behind a tier. If it is in Stream-Cut it is
        on your plan.</p>
    </details>`;
}

export async function accountView(root: HTMLElement) {
  // In parallel: the page needs both and neither depends on the other. The
  // catalogue is allowed to fail — a server with no plans table reachable can
  // still show an account, it just cannot offer an upgrade.
  const [me, catalog] = await Promise.all([
    api<Me>("GET", "/v1/me"),
    api<{ plans: PlanRow[] }>("GET", "/v1/plans").catch(() => ({ plans: [] })),
  ]);
  const pct = Math.min(100, Math.round((me.storage.used_bytes / me.storage.quota_bytes) * 100));

  root.innerHTML = `
    <header class="page-head"><h1>Account</h1></header>
    <div id="verifyBanner"></div>
    <div id="accessBanner"></div>

    <section class="card">
      <h2>Plan</h2>
      <p class="meta">${esc(me.email)} · ${esc(me.plan.name)}</p>
      <div class="bar"><i style="width:${pct}%"></i></div>
      <p class="meta">${gb(me.storage.used_bytes)} GB of ${gb(me.storage.quota_bytes)} GB used${
        me.recordings.limit !== null
          ? ` · ${me.recordings.used_this_month} of ${me.recordings.limit} streams this month`
          : ""
      }</p>
      <div class="actions" id="planActions"></div>
      <p class="meta" id="billingNote"></p>
      ${includedBlock()}
      <p class="meta">Billing questions: <a href="mailto:billing@stream-cut.com">billing@stream-cut.com</a> &middot; Anything else: <a href="mailto:support@stream-cut.com">support@stream-cut.com</a></p>
    </section>

    <section class="card">
      <h2>Auto-captions</h2>
      <p class="meta">Transcribe each clip and burn in captions as it renders — the way most
        reaction clips are watched on mute. A <strong>.vtt</strong> caption file is saved
        alongside every captioned clip so you can restyle it in an editor. Off by default.</p>
      <label class="toggle">
        <input type="checkbox" id="captions"> Caption my clips
      </label>
      <p class="meta" id="captionsNote"></p>
    </section>

    <section class="card">
      <h2>Vertical clips</h2>
      <p class="meta">Reframe each clip to vertical <strong>9:16</strong> for TikTok, Reels and
        Shorts — a centred crop of the picture, with your logo and captions placed on the
        vertical frame. Leave it off to keep your landscape clips. Off by default.</p>
      <label class="toggle">
        <input type="checkbox" id="reframe"> Make my clips vertical
      </label>
      <p class="meta" id="reframeNote"></p>
    </section>

    <section class="card">
      <h2>Links in chat</h2>
      <p class="meta">When a promotion comes on your stream, its link is posted in your chat from
        your own account, so viewers can click it right there. At most one link every 3 minutes,
        and the same product at most every 15. Twitch and Kick; YouTube is planned, and TikTok
        doesn't allow it.</p>
      <div id="chatLinks" class="loading">Loading…</div>
    </section>

    <section class="card">
      <h2>Connect Stream-Cut Studio</h2>
      <p class="meta">Generate a code, then type it into Stream-Cut Studio on the machine you stream from.
        The code lasts ten minutes and works once.</p>
      <div id="pairBox"></div>
      <div class="actions"><button class="btn" id="pair">Get a pairing code</button></div>
    </section>

    <section class="card">
      <h2>Connected machines</h2>
      <p class="meta">Revoking one signs that machine out immediately. It will ask for a new
        pairing code next time it opens.</p>
      <div id="devices" class="loading">Loading…</div>
    </section>
  `;

  renderVerify();
  renderAccess();
  void renderDevices();
  void renderChatLinks(root.querySelector<HTMLElement>("#chatLinks")!);
  // --- Clip output toggles (captions, vertical reframe) -------------------
  // Both are plain per-user booleans the clip renderer reads, saved on change.

  void wireToggle("captions", "/v1/captions", "On. New clips will be captioned as they render.");
  void wireToggle("reframe", "/v1/reframe", "On. New clips will render in vertical 9:16.");

  async function wireToggle(id: string, path: string, onNote: string) {
    const box = root.querySelector<HTMLInputElement>(`#${id}`)!;
    const note = root.querySelector<HTMLElement>(`#${id}Note`)!;
    try {
      const { enabled } = await api<{ enabled: boolean }>("GET", path);
      box.checked = enabled;
    } catch {
      /* leave unchecked */
    }
    box.addEventListener("change", async () => {
      box.disabled = true;
      note.textContent = "Saving…";
      try {
        const { enabled } = await api<{ enabled: boolean }>("PUT", path, { enabled: box.checked });
        box.checked = enabled;
        note.textContent = enabled ? onNote : "Off.";
      } catch (err) {
        box.checked = !box.checked;
        note.textContent = (err as Error).message;
      } finally {
        box.disabled = false;
      }
    });
  }

  // --- Email verification -------------------------------------------------

  function renderVerify() {
    const el = root.querySelector<HTMLElement>("#verifyBanner")!;
    if (me.email_verified) {
      el.innerHTML = "";
      return;
    }
    // The trial does not start until the address is confirmed, so this is the
    // most important thing on the page for an unverified account.
    el.innerHTML = `
      <div class="banner banner-warn">
        <div>
          <strong>Confirm your email to start your trial</strong>
          <p class="meta">We sent a link to ${esc(me.email)}. Your 14 days start when you
            click it, not now — so there's no rush.</p>
        </div>
        <button class="btn btn-quiet" id="resend">Resend</button>
      </div>`;

    el.querySelector("#resend")!.addEventListener("click", async (e) => {
      const btn = e.currentTarget as HTMLButtonElement;
      btn.disabled = true;
      btn.textContent = "Sending…";
      try {
        await api("POST", "/v1/auth/verify/resend");
        btn.textContent = "Sent";
      } catch (err) {
        btn.disabled = false;
        btn.textContent = "Resend";
        alert((err as Error).message);
      }
    });
  }

  // --- Trial / billing state ----------------------------------------------

  function renderAccess() {
    const el = root.querySelector<HTMLElement>("#accessBanner")!;
    const a = me.access;
    if (!a || a.state === "active") {
      el.innerHTML = "";
      return;
    }

    // Work already recorded but not yet uploaded is the strongest reason to
    // subscribe, so it is said plainly rather than buried.
    const pending =
      me.pending.reactions > 0
        ? ` You have ${me.pending.reactions} reactions recorded that haven't become clips yet —
           subscribe and those recordings will still generate every one of them, as long as you
           still have the OBS files.`
        : "";

    const copy: Record<string, { cls: string; title: string; body: string }> = {
      // Not a lapsed trial — one that has not begun. The only thing being asked
      // for here is a click in an inbox, so that is the whole message.
      unverified: {
        cls: "banner-info",
        title: "Confirm your email to start your trial",
        body: "We sent you a link. Your 14-day trial and 5 streams begin once you click it.",
      },
      // Days remaining is the wrong headline once the uploads are gone: the
      // upload cap is what actually ends most trials, and a banner counting
      // down days reads as "you still have time" when you do not.
      trialing: me.recordings.at_limit
        ? {
            cls: "banner-warn",
            title: "You've used all 5 trial streams",
            body: `Subscribe to turn this stream into clips.${pending}`,
          }
        : {
            cls: "banner-info",
            title: `Trial — ${a.trial_days_left} ${a.trial_days_left === 1 ? "day" : "days"} left`,
            body: `${me.recordings.remaining} of ${me.recordings.limit} streams remaining.${pending}`,
          },
      past_due: {
        cls: "banner-warn",
        title: "We couldn't take your payment",
        body: "Nothing has changed yet — you have a week of full access while you update your card.",
      },
      expired: {
        cls: "banner-warn",
        title: "Your trial has ended",
        body: `Stream-Cut Studio won't start new sessions until you subscribe.${
          a.can_redeem_pending ? pending : ""
        }`,
      },
    };

    const c = copy[a.state]!;
    el.innerHTML = `<div class="banner ${c.cls}">
      <div><strong>${c.title}</strong><p class="meta">${c.body}</p></div>
    </div>`;
  }

  // --- Billing ------------------------------------------------------------


  const money = (cents: number) =>
    cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;

  const planLabel = (id: string) => {
    const p = catalog.plans.find((x) => x.id === id);
    return p ? `${p.display_name} — ${money(p.price_cents)}/mo` : "Subscribe";
  };

  /**
   * One line of what a plan limits.
   *
   * Only the limits that differ, and said as a quantity rather than a tick:
   * every plan has every feature, so “what do I get” is always a number here.
   */
  function planLimits(p: PlanRow): string {
    const parts: string[] = [];
    if (p.max_recordings_per_month != null) parts.push(`${p.max_recordings_per_month} streams`);
    parts.push(
      p.max_clips_per_recording != null
        ? `${p.max_clips_per_recording} clips each`
        : "every clip",
    );
    parts.push(`${Math.round(Number(p.storage_quota_bytes) / 1073741824)} GB`);
    return parts.join(" · ");
  }

  /**
   * One button per purchasable plan, or a single Manage button once something
   * is active.
   *
   * Rendered rather than written into the markup because it used to be a fixed
   * Subscribe button that always sent `plan: "creator"`. The marketing site
   * advertises Pro and there was no way to buy it — the Get Pro link goes to the
   * download section, and the intent was lost from there on.
   */
  function renderPlanActions() {
    const el = root.querySelector<HTMLElement>("#planActions")!;

    if (me.access?.state === "active") {
      el.innerHTML = `<button class="btn" data-billing="manage">Manage subscription</button>`;
      return;
    }

    // A server that cannot serve the catalogue can still take a subscription;
    // it just cannot offer the choice. Better than no button at all.
    if (catalog.plans.length === 0) {
      el.innerHTML = `<button class="btn" data-billing="creator">Subscribe</button>`;
      return;
    }

    // Cheapest first, and the cheapest is the solid one: it is where nearly
    // everyone should start.
    //
    // Each button says what the plan limits, because the plans no longer differ
    // only in size. Starter makes ten clips from each stream and the others make
    // all of them, and somebody should not have to find that out after paying.
    el.innerHTML = catalog.plans
      .map(
        (p, i) =>
          `<button class="btn ${i === 0 ? "" : "btn-outline"}" data-billing="${esc(p.id)}"
                   title="${esc(planLimits(p))}">
             ${esc(p.display_name)} — ${money(p.price_cents)}/mo
             <small>${esc(planLimits(p))}</small>
           </button>`,
      )
      .join("");
  }

  renderPlanActions();

  // Delegated: the buttons are re-rendered when the plan changes, and handlers
  // bound to the replaced nodes would quietly stop working.
  root.querySelector("#planActions")!.addEventListener("click", async (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("[data-billing]");
    if (!btn) return;

    const which = btn.dataset.billing!;
    const note = root.querySelector<HTMLElement>("#billingNote")!;
    const restore = which === "manage" ? "Manage subscription" : planLabel(which);
    const all = Array.from(
      root.querySelectorAll<HTMLButtonElement>("#planActions [data-billing]"),
    );

    // Every button, not just the clicked one. Two checkouts open at once is a
    // way to be charged twice.
    all.forEach((b) => (b.disabled = true));
    btn.textContent = "Opening…";
    note.textContent = "";

    try {
      const deps = { api };
      const result =
        which === "manage"
          ? await manageBilling(deps)
          : await startCheckout(which, deps);

      if (result.mode === "redirect") {
        location.href = result.url;
        return;
      }

      // Paddle's overlay opens on top of this page and does not navigate it,
      // so the buttons have to come back. Without this, a streamer who closes
      // the overlay to think about it is left holding dead controls and has to
      // reload to try again. Completion is handled by the event listener
      // below, not here.
      all.forEach((b) => (b.disabled = false));
      btn.textContent = restore;
    } catch (err) {
      all.forEach((b) => (b.disabled = false));
      btn.textContent = restore;
      // A missing provider is a deployment state, not a user error — say which.
      note.textContent =
        (err as { status?: number }).status === 404
          ? "Billing isn't configured on this server yet."
          : (err as Error).message;
    }
  });

  /**
   * Paddle sends the browser back here the moment payment clears, which is
   * before the webhook that actually grants access has arrived — usually by a
   * second, occasionally by more. Rendered state at that instant still says
   * the trial has ended, so a streamer who has just paid would be told to
   * subscribe again. Waiting it out is worth the fifteen seconds.
   */
  async function confirmSubscription() {
    const el = root.querySelector<HTMLElement>("#accessBanner")!;
    el.innerHTML = `<div class="banner banner-info"><div>
      <strong>Payment received — activating your account…</strong>
      <p class="meta">This usually takes a few seconds.</p></div></div>`;

    for (let i = 0; i < 10; i++) {
      await new Promise((r) => setTimeout(r, 1500));
      let fresh: Me;
      try {
        fresh = await api<Me>("GET", "/v1/me");
      } catch {
        continue; // A blip mid-poll is not a failed payment.
      }
      if (fresh.access?.state === "active") {
        me.access = fresh.access;
        me.plan = fresh.plan;
        me.storage = fresh.storage;
        me.recordings = fresh.recordings;
        // Swaps the plan buttons for Manage, now that there is one to manage.
        renderPlanActions();
        el.innerHTML = `<div class="banner banner-ok"><div>
          <strong>You're subscribed — thank you.</strong>
          <p class="meta">${esc(me.plan.name)} is active. Upload the recordings you were
            holding and they will generate every clip.</p></div></div>`;
        return;
      }
    }

    // Deliberately not an error: the payment went through, so the one thing
    // this must not do is suggest paying again.
    el.innerHTML = `<div class="banner banner-info"><div>
      <strong>Payment received — still activating.</strong>
      <p class="meta">Reload this page in a minute. If it still says this, email
        <a href="mailto:billing@stream-cut.com">billing@stream-cut.com</a> and we will sort it
        out — you will not be charged twice.</p></div></div>`;
  }

  /**
   * How a completed payment is noticed.
   *
   * Not a redirect: Paddle refuses a return URL whose domain it has not
   * approved, which rules out localhost outright and would rule out any
   * dashboard subdomain that had not been through approval separately. The
   * overlay reports completion through this callback on any origin.
   */
  onCheckoutEvent((e) => {
    if (e.name === "checkout.completed" && me.access?.state !== "active") {
      void confirmSubscription();
    }
  });

  // Still honoured, for a deployment that does pass an approved successUrl.
  if (location.hash.includes("subscribed=1") && me.access?.state !== "active") {
    void confirmSubscription();
  }

  // --- Pairing ------------------------------------------------------------

  root.querySelector("#pair")!.addEventListener("click", async (e) => {
    const btn = e.currentTarget as HTMLButtonElement;
    const box = root.querySelector<HTMLElement>("#pairBox")!;
    btn.disabled = true;
    btn.textContent = "Generating…";

    try {
      const { code, expires_in } = await api<{ code: string; expires_in: number }>(
        "POST", "/v1/auth/pair",
      );
      // Split into two groups: an eight-character run is hard to read off a
      // screen and easy to lose your place in while typing.
      const shown = `${code.slice(0, 4)} ${code.slice(4)}`;
      box.innerHTML = `
        <div class="pairout">
          <code id="pairCode">${esc(shown)}</code>
          <span class="meta" id="pairTimer"></span>
        </div>
        <p class="meta">Open Stream-Cut Studio and type this in. Spaces don't matter.</p>`;

      let left = expires_in;
      const timer = box.querySelector<HTMLElement>("#pairTimer")!;
      const tick = () => {
        if (left <= 0) {
          box.innerHTML = `<p class="meta">That code expired. Generate another.</p>`;
          clearInterval(iv);
          return;
        }
        timer.textContent = `expires in ${Math.floor(left / 60)}:${String(left % 60).padStart(2, "0")}`;
        left--;
      };
      const iv = setInterval(tick, 1000);
      tick();
    } catch (err) {
      alert((err as Error).message);
    } finally {
      btn.disabled = false;
      btn.textContent = "Get another code";
    }
  });

  // --- Devices ------------------------------------------------------------

  async function renderDevices() {
    const el = root.querySelector<HTMLElement>("#devices")!;
    const { devices } = await api<{ devices: Device[] }>("GET", "/v1/auth/devices");
    el.className = "";

    if (!devices.length) {
      el.innerHTML = `<p class="meta">No machines connected yet.</p>`;
      return;
    }

    el.innerHTML = devices
      .map(
        (d) => `<div class="row">
          <div class="row-main">
            <strong>${esc(d.label ?? "Stream-Cut Studio")}</strong>
            <span class="meta">Added ${new Date(d.created_at).toLocaleDateString()}${
              d.last_used_at
                ? ` · last used ${new Date(d.last_used_at).toLocaleDateString()}`
                : " · never used"
            }</span>
          </div>
          <button class="btn btn-quiet" data-revoke="${esc(d.id)}">Revoke</button>
        </div>`,
      )
      .join("");

    el.querySelectorAll<HTMLButtonElement>("[data-revoke]").forEach((btn) => {
      btn.addEventListener("click", async () => {
        if (!confirm("Sign this machine out of Stream-Cut Studio?")) return;
        btn.disabled = true;
        await api("DELETE", `/v1/auth/devices/${btn.dataset.revoke}`);
        await renderDevices();
      });
    });
  }
}
